import type {
  Layout,
  LayoutGrid,
  LayoutGridComponents,
  LayoutGridKey,
  Layouts,
} from './types';

const COLUMNS = 12;

const totalWidth = <T>(row: Layout<T>[]): number =>
  row.reduce((sum, item) => sum + item.w, 0);

/** Splits a layout into rows, each a maximal run of consecutive items that fits in the grid's columns. */
function rows<T>(layout: Layout<T>[]): Layout<T>[][] {
  const result: Layout<T>[][] = [];
  let width = COLUMNS;
  for (const item of layout) {
    if (width + item.w > COLUMNS) {
      result.push([]);
      width = 0;
    }
    result[result.length - 1].push(item);
    width += item.w;
  }
  return result;
}

/** Scales a row's widths in proportion to fill `width` columns, giving the rounding leftovers to the largest remainders. */
function widen<T>(row: Layout<T>[], width: number): Layout<T>[] {
  const total = totalWidth(row);
  const scaled = row.map((item) => (item.w * width) / total);
  const widths = scaled.map((w) => Math.floor(w));
  const spare = width - widths.reduce((sum, w) => sum + w, 0);
  row
    .map((_, index) => index)
    .sort((a, b) => scaled[b] - widths[b] - (scaled[a] - widths[a]))
    .slice(0, spare)
    .forEach((index) => {
      widths[index] += 1;
    });
  return row.map((item, index) => ({
    ...item,
    w: widths[index] as Layout<T>['w'],
  }));
}

/** Removes the items `hide` selects from a grid, widening the rest of each row in proportion so the row keeps its width. */
export function withoutItems<T extends string>(
  grid: LayoutGrid<T>,
  hide: (item: T) => boolean
): LayoutGrid<T> {
  const components = Object.fromEntries(
    Object.entries(grid.components).filter(([item]) => !hide(item as T))
  ) as LayoutGridComponents<T>;

  const layouts = Object.fromEntries(
    (Object.keys(grid.layouts) as LayoutGridKey[]).map((key) => [
      key,
      rows(grid.layouts[key]).flatMap((row) => {
        const kept = row.filter((item) => !hide(item.i));
        return kept.length === row.length ? row : widen(kept, totalWidth(row));
      }),
    ])
  ) as Layouts<T>;

  return { components, layouts };
}
